import React, { useContext, useEffect, useRef, useState } from 'react'
import Select from 'react-select'
import { CustomerContext } from '../../../contexts/CustomerContext'
import useSchools from '../../../hooks/useSchools'
import Typography from '../../Typography'
import classes from './schedule-appointment.module.sass'
import Button from '../../Button'
import Cart from '../../../models/Cart'
import StudentIdForm from '../../StudentIdForm'
import ShootingSchedule from '../../../models/ShootingSchedule'
import { routes } from '../../../routes'
import { SchoolType } from '../../../models/_models'
import { useHistory } from 'react-router-dom'
import useQueryString from '../../../hooks/use-query-string'
import OtherSchoolTypeForm from './OtherSchoolTypeForm';
import createdLocationForAppointment from './createdLocationForAppointment';
import { toast, ToastContainer } from 'react-toastify';
import apptHeaderImg from './make-appt-header.jpg';
import seniorSidePhoto from './modesto-senior-portrait-photographer-appointment.jpg';
import Package from '../../../models/Package';
import { OrderPackage, Student } from '../../../models';

const SCHEDULE_AT_SCHOOL = 'SCHEDULE_AT_SCHOOL'
const SCHEDULE_AT_SHOOB = 'SCHEDULE_AT_SHOOB'
const NO_SCHEDULES_AVAILABLE = 'NO_SCHEDULES_AVAILABLE'

export const customStyles = {
  control: (provided) => ({
    ...provided,
    border: '1px solid #aaa',
    borderRadius: 0,
    minHeight: 48,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
};

const ScheduleAppointment = () => {
  const { customer, setCustomer } = useContext(CustomerContext)
  const history = useHistory()
  const { apptCart, currentSchool } = customer
  const schools = useSchools()
  const [school, setSchool] = useState('')
  const [email, setEmail] = useState(customer.email || '')
  const [studentId, setStudentId] = useState('');
  const [scheduleStatus, setScheduleStatus] = useState(SCHEDULE_AT_SCHOOL)
  const [step, setStep] = useState(0)
  const [schoolType, setSchoolType] = useState('')
  // `from` tags the cart's created_location (Cart#set_created_location
  // normalizes it; e.g. from=sitting_recovery on the abandoned-cart reminder).
  const { p: packageId, senior, message_id: messageId, from } = useQueryString();

  const apptText = senior ? 'Enter your information to view available appointments and schedule your Senior Portraits' : 'Enter your information to view available appointments for Studio Pictures';

  const [hasPackage, setHasPackage] = useState(true);
  const [apptPackage, setApptPackage] = useState();
  const [apptPackageLoading, setApptPackageLoading] = useState(false);
  // Inline error shown in the form when no appointment package is configured
  // for the selected school. Avoids the double-toast at the bottom-right that
  // happens when this effect fires for both `school` and `schoolType` changes.
  const [noAppointmentsError, setNoAppointmentsError] = useState(false);
  // Inline lookup state for the student-id field. Lets us confirm the student
  // by name before the customer hits Continue (and cache the result so we
  // don't double-fetch on submit).
  // status: 'idle' | 'loading' | 'found' | 'not_found' | 'error'
  const [studentLookup, setStudentLookup] = useState({ status: 'idle', student: null, key: null });
  // Guards against `proceedWithStudent` running twice when both the inline
  // blur-lookup and the submit handler race to advance to the next page.
  const proceedingRef = useRef(false);

  useEffect(() => {
    if (!school.value) return;
    /* should look for Fall E1 package for elementary */
    /* should look for appointment package / sittings for seniors */
    setApptPackage(undefined);
    setApptPackageLoading(true);
    setNoAppointmentsError(false);
    const onErr = (err) => {
      console.error('appt package fetch failed', err);
      toast.error('Could not load appointment options. Please refresh and try again.', { autoClose: false });
      setApptPackageLoading(false);
    };
    if (senior) {
      // Senior bookings resolve their pricelist by season SERVER-SIDE, so a
      // stale hardcoded `p` from old nav links can't route bookings onto last
      // season's (school-less) senior package. See Package.current_senior_sessions.
      // Pass the student's school so a school on a special senior package
      // (e.g. #910 with a free yearbook sitting) gets THAT package instead
      // of the majority-schools default (#4720).
      Package
        .where({ seniorSessionsForSchool: school.value })
        .all()
        .then((res) => {
          setApptPackageLoading(false);
          if (res.data.length < 1) {
            setNoAppointmentsError(true);
            return;
          }
          setApptPackage(res.data[0]);
        })
        .catch(onErr);
    } else if (packageId) {
      Package
        .find(packageId)
        .then((res) => {
          setApptPackageLoading(false);
          if (!res) {
            setNoAppointmentsError(true);
            return;
          }
          setApptPackage(res.data);
        })
        .catch(onErr);
    } else if (schoolType.toLowerCase().includes('elementary')) {
      Package
        .where({ schoolId: school.id, elementarySittings: true })
        .all()
        .then((res) => {
          setApptPackageLoading(false);
          if (res.data.length < 1) {
            setNoAppointmentsError(true);
            return;
          }
          setApptPackage(res.data[0]);
        })
        .catch(onErr);
    } else {
      Package
        .where({ schoolId: school.id, highSchoolSittings: true })
        .order({ shouldShowAppointments: false })
        .all()
        .then((res) => {
          setApptPackageLoading(false);
          if (res.data.length < 1) {
            setNoAppointmentsError(true);
            return;
          }
          setApptPackage(res.data[0]);
        })
        .catch(onErr);
    }
  }, [school, schoolType]);

  useEffect(() => {
    async function getData() {
      if (!school) return

      const { data: schoolScheduleData } = await ShootingSchedule
        .where({ inFuture: true, schoolId: school.value })
        .all()

      const { data: shoobScheduleData } = await ShootingSchedule
        .where({ inFuture: true, schoolId: 1075 })
        .all()

      if (schoolScheduleData.length > 0) {
        setScheduleStatus(SCHEDULE_AT_SCHOOL)
      } else if (shoobScheduleData.length > 0) {
        setScheduleStatus(SCHEDULE_AT_SHOOB)
      } else {
        setScheduleStatus(NO_SCHEDULES_AVAILABLE)
      }
    }

    getData()
  }, [school])

  useEffect(() => {
    if (!school) return

    SchoolType
      .find(school.schoolTypeId)
      .then((res) => setSchoolType(res.data.name))
      console.log(schoolType);
  }, [school])

  let route
  if ([SCHEDULE_AT_SHOOB, NO_SCHEDULES_AVAILABLE].includes(scheduleStatus)) {
    const status = scheduleStatus === SCHEDULE_AT_SHOOB ? 0 : 1
    const t = schoolType.toLowerCase().includes('elementary') ? 0 : 1
    // Prefer the resolved appointment package (for senior=true this is the
    // season-resolved seniorSessions package, since `p` is intentionally
    // ignored / absent on senior nav links); fall back to the URL `p`.
    route = routes.scheduleAtShoob({ status, t, s: school.value, p: apptPackage?.id || packageId })
  }

  // Cache key — invalidates when school or studentId changes.
  const lookupKey = school?.id && studentId ? `${school.id}:${studentId}` : null;

  // Look up the student by school + student_id and store the result so we can
  // (a) confirm the customer's identity inline before they click Continue,
  // and (b) skip a duplicate fetch on submit.
  // The is_enrolled filter that used to live on this query was excluding
  // transfers and make-up appointment seekers (the customers most likely to
  // need this form). school + student_id is enough for the studio-appt path.
  const lookupStudentByIdField = () => {
    if (!school?.id || !studentId) return;
    // If we already have a successful lookup for this exact school + ID, skip
    // the refetch. This matters when the user clicks Continue: blurring the
    // input triggers this handler before the click reaches the submit button,
    // and re-setting status to 'loading' caused the submit to fail silently.
    if (studentLookup.key === lookupKey && studentLookup.status === 'found') return;
    setStudentLookup({ status: 'loading', student: null, key: lookupKey });

    Student
      .where({ schoolId: school.id, studentId })
      .first()
      .then((res) => {
        if (res.data) {
          setStudentLookup({ status: 'found', student: res.data, key: lookupKey });
          // Auto-advance to package options the moment we have a confirmed
          // student plus everything else the next step needs. Saves the user
          // from having to also click Continue.
          if (school?.value && email && apptPackage) {
            toast.success('We found your student! Redirecting…', { autoClose: 4000 });
            proceedWithStudent(res.data);
          }
        } else {
          setStudentLookup({ status: 'not_found', student: null, key: lookupKey });
        }
      })
      .catch((err) => {
        console.error('student lookup failed', err);
        setStudentLookup({ status: 'error', student: null, key: lookupKey });
      });
  };

  const proceedWithStudent = (foundStudent) => {
    if (proceedingRef.current) return;
    proceedingRef.current = true;

    const orderPackage = new OrderPackage({
      package: apptPackage,
      packageId: apptPackage.id,
      student: foundStudent,
      studentId: foundStudent.id,
    });

    const selectedSchool = schools.find((sSchool) => sSchool.id === school.value);
    const onCartFail = (err) => {
      proceedingRef.current = false;
      console.error('cart save failed', err);
      toast.error('We could not start your appointment. Please refresh and try again.', { autoClose: false });
    };

    if (apptCart) {
      apptCart.schoolId = school.id;
      apptCart.messageId = messageId;
      const recoveredLocation = createdLocationForAppointment(from, true);
      if (recoveredLocation) apptCart.createdLocation = recoveredLocation;
      apptCart.save().then(() => {
        orderPackage.cartId = apptCart.id;
        setCustomer({ ...customer, currentSchool: selectedSchool, currentStudent: foundStudent, email, currentOrderPackage: orderPackage });
        history.push(routes.appointmentOptionSelection(apptPackage.id, senior ? { senior: 'true' } : {}));
      }).catch(onCartFail);
    } else {
      const cart = new Cart({
        createdLocation: createdLocationForAppointment(from, false),
        email,
        school: selectedSchool,
        schoolId: school.value,
        messageId,
      });
      cart.save().then(() => {
        orderPackage.cartId = cart.id;
        setCustomer({
          ...customer, apptCart: cart, email, currentSchool: selectedSchool, currentStudent: foundStudent, currentOrderPackage: orderPackage
        });
        history.push(routes.appointmentOptionSelection(apptPackage.id, senior ? { senior: 'true' } : {}));
      }).catch(onCartFail);
    }
  };

  const submitHandler = (e) => {
    e.preventDefault();

    if (!school?.value) {
      toast.error('Please select your school.');
      return;
    }
    if (email === '') {
      toast.error('Please enter your email address');
      return;
    }
    if (studentId === '') {
      toast.error('Please enter a Student I.D. #');
      return;
    }
    if (apptPackageLoading) {
      toast.info('Loading appointment options for your school — please wait a moment and try again.');
      return;
    }
    if (!apptPackage) {
      // The inline form alert already explains this; just block submission.
      setNoAppointmentsError(true);
      return;
    }

    // If we already looked up this exact student and have them cached, skip
    // the round-trip and proceed straight to checkout.
    if (studentLookup.key === lookupKey && studentLookup.status === 'found' && studentLookup.student) {
      toast.success('Redirecting you to package options…', { autoClose: 5000 });
      proceedWithStudent(studentLookup.student);
      return;
    }

    Student
      .where({ schoolId: school.id, studentId })
      .first()
      .then((res) => {
        if (!res.data) {
          // Inline banner above the form already surfaces this — no toast.
          setStudentLookup({ status: 'not_found', student: null, key: lookupKey });
          return;
        }
        setStudentLookup({ status: 'found', student: res.data, key: lookupKey });
        toast.success('We found your student! Please wait while we redirect you to package options.', { autoClose: 10000 });
        proceedWithStudent(res.data);
      })
      .catch((err) => {
        console.error('student lookup failed', err);
        setStudentLookup({ status: 'error', student: null, key: lookupKey });
      });
  };

  return (
    <>
      <div className={`${classes.pageContainer} ${classes.senior}`}>
        {!senior && <img className={`${classes.desktopImg} ${classes.img}`} src={apptHeaderImg} />}
        <ToastContainer position="bottom-right" />
        <div className={classes.container}>
          {senior && (
            <p className={classes.seniorFormTitle}>Book Your Senior <em>Session</em></p>
          )}
          {!senior && (
            <div className={classes.seniorApptText}>
              <p>Make an <em>Appointment</em></p>
              <p>{apptText}</p>
            </div>
          )}
          <div>
            {step === 1 && schoolType !== 'Other' && hasPackage && (
              <Typography classes={classes.instructions} align="center">
                To view available appointments, enter student I.D.
              </Typography>
            )}
            {step === 0 && senior && (
              <ol className={classes.steps} aria-label="Booking progress">
                <li className={`${classes.stepItem} ${classes.stepItemActive}`}>
                  <span className={classes.stepNum}>
                    <span className={classes.stepDigit}>1</span>
                    <svg className={classes.stepIcon} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M12 12a4 4 0 100-8 4 4 0 000 8zm0 2c-4 0-7 2-7 5v1h14v-1c0-3-3-5-7-5z"/>
                    </svg>
                  </span>
                  <span className={classes.stepLabel}>Find your student</span>
                </li>
                <li className={classes.stepItem}>
                  <span className={classes.stepNum}>
                    <span className={classes.stepDigit}>2</span>
                    <svg className={classes.stepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="3" y="5" width="18" height="16" rx="2"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                      <line x1="8" y1="3" x2="8" y2="7"/>
                      <line x1="16" y1="3" x2="16" y2="7"/>
                    </svg>
                  </span>
                  <span className={classes.stepLabel}>Choose a time</span>
                </li>
                <li className={classes.stepItem}>
                  <span className={classes.stepNum}>
                    <span className={classes.stepDigit}>3</span>
                    <svg className={classes.stepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="5 12 10 17 19 7"/>
                    </svg>
                  </span>
                  <span className={classes.stepLabel}>Confirm</span>
                </li>
              </ol>
            )}
            {step === 0 && (
              <form onSubmit={submitHandler} className={classes.form}>
                {noAppointmentsError && (
                  <div className={classes.formAlert} role="alert">
                    There are no appointments available for your school at this time.
                  </div>
                )}
                {studentLookup.key === lookupKey && studentLookup.status === 'not_found' && (
                  <div className={classes.formAlert} role="alert">
                    We couldn't find a student with that I.D. at this school. Please double-check and try again.
                  </div>
                )}
                {studentLookup.key === lookupKey && studentLookup.status === 'error' && (
                  <div className={classes.formAlert} role="alert">
                    Something went wrong looking up your student. Please try again.
                  </div>
                )}
                <label className={`${classes.label} ${classes.select}`}>
                  <span>Your School:</span>
                  <Select
                    styles={customStyles}
                    defaultValue={school}
                    options={schools.map((sSchool) => ({
                      value: sSchool.id,
                      label: sSchool.name,
                      schoolTypeId: sSchool.schoolTypeId,
                      id: sSchool.id
                    }))}
                    onChange={setSchool}
                  />
                </label>
                <label className={classes.label}>
                  <span>Your Email Address:</span>
                  <input type="email" placeholder="Email" value={email} onChange={(evt) => setEmail(evt.target.value)} />
                </label>
                <label className={classes.label}>
                  <span>Student I.D. #:</span>
                  <input type="text" placeholder="Student I.D." value={studentId}
                         onChange={(e) => {
                           setStudentId(e.target.value);
                           // typing invalidates a stale lookup
                           if (studentLookup.status !== 'idle') setStudentLookup({ status: 'idle', student: null, key: null });
                         }}
                         onBlur={lookupStudentByIdField} />
                  {studentLookup.key === lookupKey && (studentLookup.status === 'loading' || studentLookup.status === 'found') && (
                    <small style={{ display: 'block', marginTop: 4, fontSize: 13 }}>
                      {studentLookup.status === 'loading' && (
                        <span style={{ color: '#475569' }}>Looking up student…</span>
                      )}
                      {studentLookup.status === 'found' && (
                        <span style={{ color: '#15803d' }}>
                          ✓ Found: {studentLookup.student.firstName} {studentLookup.student.lastName}
                        </span>
                      )}
                    </small>
                  )}
                </label>
                <Button fullWidth size="large" type="submit" variant="primary"
                        disabled={apptPackageLoading || studentLookup.status === 'loading'}>
                  {apptPackageLoading ? 'Loading appointment options…' : 'Continue'}
                </Button>
              </form>
            )}
            {step === 1 && schoolType === 'Other' && <OtherSchoolTypeForm school={school} />}
            {step === 1 && schoolType !== 'Other' &&
              <StudentIdForm app isAppointments route={route} school={school} schoolType={schoolType}
                             setHasPackage={setHasPackage} />}
          </div>
        </div>
        {senior && <img className={classes.seniorSidePhoto} src={seniorSidePhoto} alt="Senior portrait session" />}
        {!senior && <img className={`${classes.mobileImg} ${classes.img}`} src={apptHeaderImg} />}
      </div>
    </>
  )
}

export default ScheduleAppointment
